import React, { useEffect, useMemo, useState } from 'react';
import {
  Sliders, Save, RefreshCw, Trash2, Calculator, Calendar, Target,
  AlertTriangle, ShieldCheck, Info, Building2, Briefcase, User,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState } from '../../index';
import { InheritanceChain } from '../InheritanceChain';
import { DeleteScoringOverrideModal } from '../modals';
import { useAuth } from '../../../../contexts/AuthContext';
import { useActiveScope } from '../../../../hooks/useActiveScope';
import {
  getTrustScoringConfig, listTrustScoringConfigs, updateTrustScoringConfig,
  deleteTrustScoringConfig, getOrganizations, getTenantsByOrg,
} from '../../../../services/api';

/**
 * ScoringConfigTab — V2 Trust Scoring Configuration (Phase A10 / G310).
 *
 * Full V1 feature parity:
 *   - Inheritance chain visualisation (ORG → TENANT → AGENT)
 *   - CUSTOM / INHERITED status banner per scope
 *   - Scope tabs (Org, Tenant, Agent)
 *   - RBAC-aware scope selectors (Tenant + Agent picker when nested)
 *   - Full editor:
 *       evaluation multiplier (with live preview score calculation)
 *       BLOCK / WARN / AUDIT violation weights
 *       Autonomy thresholds (stacked bar visualisation)
 *       Scoring window + probation duration + auto-regress
 *       Organisational Trust Goals (Admin-only): target score,
 *       target autonomy, risk tolerance, compliance deadline,
 *       board report note
 *   - Active Config Overrides summary
 *   - Formula Reference card
 *   - Remove Override (with inheritance-preview modal)
 *
 * @param {Object} props
 * @param {Array} props.enriched       — scope-filtered agents (Agent scope picker)
 * @param {Function} props.addToast
 */
export function ScoringConfigTab({ enriched, addToast, focusAgentId, onFocusAgent }) {
  /* G324 — RBAC context. The scope selectors visible to the operator
   * depend on their role:
   *   - Super Admin: can pick any org + tenant + agent (ORG/TENANT/AGENT scopes)
   *   - Org Admin:   org pre-pinned, can pick tenant + agent (TENANT/AGENT)
   *   - Tenant Admin: tenant pre-pinned, can pick agent (AGENT only)
   *   - others: read-only effective config
   * Mirrors V1 ScoringConfigTab line 1556-1577.
   */
  const auth = useAuth();
  // Scope + active role come from the pinned SESSION, not auth.user (which never
  // carries orgId/tenantId — reading user.orgId was the empty-dropdown defect).
  const { orgId: activeOrgId, tenantId: activeTenantId } = useActiveScope();
  const userRole = auth?.session?.role || auth?.user?.role || 'Viewer';
  const userOrgId = activeOrgId;
  const userTenantId = activeTenantId;
  /* RC-84 — Include Platform Admin in the SA-or-higher gate.
   *
   * Pre-RC-84 this predicate was named `isSuperAdmin` and listed only
   * "Super Admin" + legacy aliases — Platform Admin was excluded. That
   * inverted the RBAC hierarchy: PA > SA, so anything SA can do PA can
   * also do (PA has the global "*"). The exclusion meant Platform
   * Admin viewing Trust Center → Scoring saw two visible bugs:
   *
   *   1. The org picker fell into the "non-superadmin" branch of the
   *      load effect below (filter to caller's org) — but PA has no
   *      orgId on session, so the filter returned empty and the
   *      dropdown rendered "No orgs available".
   *
   *   2. The "Only Super Admin can save Organization-scoped overrides"
   *      warning fired for PA even though they trivially out-rank SA.
   *
   * Renaming to `canSaveOrgScope` (with a back-compat alias) makes the
   * meaning explicit at every call site: this is "can manage Org-level
   * overrides", not "is the literal SA role". `isOrgAdmin` /
   * `isTenantAdmin` already cascaded off this, so they pick up PA for
   * free — which is correct (PA > OA > TA). */
  const isPlatformAdmin = userRole === 'Platform Admin' || userRole === 'platform_admin';
  const canSaveOrgScope = isPlatformAdmin
    || userRole === 'Super Admin' || userRole === 'super_admin' || userRole === 'admin';
  // Back-compat alias — keep `isSuperAdmin` callsites working without a
  // sweep through this file. Same value as canSaveOrgScope.
  const isSuperAdmin = canSaveOrgScope;
  const isOrgAdmin = isSuperAdmin || userRole === 'Org Admin' || userRole === 'org_admin';
  const isTenantAdmin = isOrgAdmin || userRole === 'Tenant Admin' || userRole === 'tenant_admin';

  /* ── State: scope, selectors, editor model ────────────────────── */
  const [scope, setScope] = useState('ORG');
  const [selectedOrgId, setSelectedOrgId] = useState(userOrgId);
  const [selectedTenantId, setSelectedTenantId] = useState(userTenantId);
  const [agentId, setAgentId] = useState(focusAgentId || '');  // seed from cross-tab focus
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allConfigs, setAllConfigs] = useState([]);
  const [effective, setEffective] = useState(null);
  /* Empty until the effective configuration arrives. This was seeded from a table of
   * platform defaults declared in this file, three of whose violation weights did not match
   * the platform's — so the editor's first paint, and the preview score beside it, showed
   * numbers the engine has never used. The effective configuration is the only source of
   * these values; the backend resolves it for every scope, including one with no override
   * anywhere, so there is nothing for a client to default. */
  const [editor, setEditor] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  /* Populate the editor from an effective configuration — the values this scope currently
   * resolves to, whether they come from an override here or are inherited from the parent.
   * Every field is taken as the backend gave it; nothing is substituted. A field that comes
   * back empty is genuinely unset, and the save omits it rather than writing a value the
   * operator never chose. */
  const seedEditorFrom = (eff) => setEditor({
    evaluationMultiplier:    eff.evaluationMultiplier,
    blockWeight:             eff.blockWeight,
    warnWeight:              eff.warnWeight,
    auditWeight:             eff.auditWeight,
    selfAutonomousThreshold: eff.selfAutonomousThreshold,
    supervisedThreshold:     eff.supervisedThreshold,
    restrictedThreshold:     eff.restrictedThreshold,
    scoreFloor:              eff.scoreFloor,
    scoringWindowDays:       eff.windowDays,
    probationDurationDays:   eff.probationDays,
    trustHistoryRetentionDays: eff.trustHistoryRetentionDays,
    autoRegress:             eff.autoRegressionOnBlock,
    // ── Conformance Scoring (The Crossing, Slice B) ──
    conformanceComparatorMode: eff.conformanceComparatorMode,
    requiredConformance:       eff.requiredConformance,
    conformanceSampleSize:     eff.conformanceSampleSize,
    conformanceWeight:         eff.conformanceWeight,
    shadowConformanceEnabled:  eff.shadowConformanceEnabled,
    shadowSamplingRate:        eff.shadowSamplingRate,
    // ── Materiality → autonomy rigor (Lever A, V339) ──
    materialityAutonomyCouplingEnabled: eff.materialityAutonomyCouplingEnabled,
    materialityThresholdStep:           eff.materialityThresholdStep,
    materialityMinEvalsStep:            eff.materialityMinEvalsStep,
    targetTrustScore:        eff.targetTrustScore,
    targetAutonomyLevel:     eff.targetAutonomyLevel,
    orgRiskTolerance:        eff.orgRiskTolerance,
    complianceDeadline:      eff.complianceDeadline
                               ? new Date(eff.complianceDeadline).toISOString().slice(0, 10) : null,
    boardReportNote:         eff.boardReportNote,
  });

  /* G324 — Load orgs once (filtered per RBAC). Super Admin sees all;
   * everyone else sees only their org. Mirrors V1 lines 1580-1599. */
  useEffect(() => {
    (async () => {
      try {
        const res = await getOrganizations();
        const all = Array.isArray(res) ? res : (res?.data || res?.organizations || []);
        if (isSuperAdmin) {
          setOrgs(all);
        } else if (userOrgId) {
          setOrgs(all.filter((o) => o.id === userOrgId));
        } else {
          setOrgs([]);
        }
        if (!isSuperAdmin && userOrgId) setSelectedOrgId(userOrgId);
        else if (isSuperAdmin && !selectedOrgId && all.length > 0) setSelectedOrgId(all[0].id);
      } catch (_) { /* non-fatal; selectors degrade to disabled */ }
    })();
    /* eslint-disable-next-line */
  }, [userOrgId, isSuperAdmin]);

  /* G324 — Load tenants when org changes. RBAC: Super/Org Admin see
   * all tenants in that org; Tenant Admin sees only their tenant.
   * Mirrors V1 lines 1601-1624. */
  useEffect(() => {
    if (!selectedOrgId) { setTenants([]); return; }
    (async () => {
      try {
        const res = await getTenantsByOrg(selectedOrgId);
        const all = Array.isArray(res) ? res : (res?.data || res?.tenants || []);
        if (isOrgAdmin) {
          setTenants(all);
        } else if (userTenantId) {
          setTenants(all.filter((t) => t.id === userTenantId));
        } else {
          setTenants([]);
        }
        if (!isOrgAdmin && userTenantId && all.find((t) => t.id === userTenantId)) {
          setSelectedTenantId(userTenantId);
        } else if (isOrgAdmin && all.length > 0 && !all.find((t) => t.id === selectedTenantId)) {
          setSelectedTenantId(all[0].id);
        }
      } catch (_) { /* non-fatal */ }
    })();
    /* eslint-disable-next-line */
  }, [selectedOrgId, userTenantId, isOrgAdmin]);

  /* Load every persisted config + the effective config for the
   * currently-selected scope. Server returns the inheritance chain
   * already resolved so we can show "what wins" without computing it
   * client-side.
   *
   * G324 — Now passes the SELECTED tenantId / agentId (not null) so
   * the backend returns the right config for the operator's context.
   */
  const load = async () => {
    setLoading(true);
    try {
      const tenantParam = scope === 'ORG' ? undefined : selectedTenantId;
      const agentParam = scope === 'AGENT' ? agentId : null;
      const [listR, effR] = await Promise.all([
        listTrustScoringConfigs(selectedTenantId || undefined).catch(() => null),
        getTrustScoringConfig(tenantParam, scope, agentParam).catch(() => null),
      ]);
      const list = Array.isArray(listR) ? listR : (listR?.data || listR?.configs || []);
      setAllConfigs(Array.isArray(list) ? list : []);
      const eff = effR?.data || effR;
      setEffective(eff || null);
      if (eff) seedEditorFrom(eff);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [scope, selectedOrgId, selectedTenantId, agentId]);

  /* G324 — Compute "is there an override at THIS scope right now?"
   * Now matches the SELECTED tenant / agent context so the
   * CUSTOM/INHERITED banner reflects the operator's chosen scope. */
  const customAt = useMemo(() => {
    const has = (s, opts = {}) => allConfigs.some((c) => {
      const cs = (c.configScope || '').toUpperCase();
      if (cs !== s) return false;
      if (s === 'AGENT')  return c.agentId === opts.agentId;
      if (s === 'TENANT') return !opts.tenantId || c.tenantId === opts.tenantId;
      /* ORG: match by orgId when present */
      return !opts.orgId || c.orgId === opts.orgId;
    });
    return {
      ORG:    has('ORG',    { orgId: selectedOrgId }),
      TENANT: has('TENANT', { tenantId: selectedTenantId }),
      AGENT:  has('AGENT',  { agentId }),
    };
  }, [allConfigs, selectedOrgId, selectedTenantId, agentId]);

  const currentScopeIsCustom = scope === 'AGENT' ? customAt.AGENT && agentId
                              : scope === 'TENANT' ? customAt.TENANT && selectedTenantId
                              : customAt.ORG;

  /* Live preview score — small calculator that shows what the editor
   * config produces for an example evaluation. Mirrors the V1 preview
   * so operators understand the formula impact before saving. */
  const previewScore = useMemo(() => {
    /* Example evaluation context: 50 evaluations, 2 blocks, 3 warns,
     * 0 audits. The score is bounded 0-100. */
    const mult = Number(editor.evaluationMultiplier) || 1;
    const block = Number(editor.blockWeight) || 0;
    const warn = Number(editor.warnWeight) || 0;
    const audit = Number(editor.auditWeight) || 0;
    const effectiveEvals = 50 * mult;
    const weightedViolations = 2 * block + 3 * warn + 0 * audit;
    // Mirrors TrustScoreMath.violationScore exactly, floor included.
    const floor = clampN(editor.scoreFloor, SCORING_BOUNDS.scoreFloor.min, SCORING_BOUNDS.scoreFloor.max);
    const raw = effectiveEvals > 0
      ? Math.max(floor, 100 - (weightedViolations / effectiveEvals) * 100)
      : 0;
    return Math.round(raw * 10) / 10;
  }, [editor]);

  /* Client-side validation that MIRRORS the TrustScoringConfig entity's
   * @Min/@DecimalMin bounds (and the request DTO that now enforces them).
   * This blocks out-of-range input at the form so the operator gets an
   * inline message instead of a round-trip 400 — and never the opaque 500
   * we used to throw when bad values reached JPA flush. Absent (null/'')
   * fields are skipped: the save omits them and the backend leaves them
   * untouched. */
  const fieldErrors = useMemo(() => {
    const errs = {};
    for (const [k, b] of Object.entries(SCORING_BOUNDS)) {
      const raw = editor[k];
      if (raw === null || raw === undefined || raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) { errs[k] = 'Must be a number'; continue; }
      if (n < b.min || n > b.max) errs[k] = `Must be between ${b.min} and ${b.max}`;
    }
    return errs;
  }, [editor]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  /* Stacked bar visualisation of autonomy thresholds. */
  const thresholdBars = useMemo(() => {
    const sa = clampN(editor.selfAutonomousThreshold, 0, 100);
    const su = clampN(editor.supervisedThreshold, 0, sa);
    const re = clampN(editor.restrictedThreshold, 0, su);
    return [
      { key: 'self',       label: 'Self-Auto',  start: sa,  end: 100, tone: 'success' },
      { key: 'supervised', label: 'Supervised', start: su,  end: sa,  tone: 'warning' },
      { key: 'restricted', label: 'Restricted', start: re,  end: su,  tone: 'warning' },
      { key: 'probation',  label: 'Probation',  start: 0,   end: re,  tone: 'danger' },
    ];
  }, [editor]);

  /* ── Handlers ─────────────────────────────────────────────────── */
  const handleSave = async () => {
    /* G324 — Validation: TENANT save requires a tenantId, AGENT save
     * requires both tenantId + agentId. Without these the backend
     * silently uses session defaults, which can clobber the wrong
     * scope. */
    if (scope === 'TENANT' && !selectedTenantId) {
      addToast?.('Pick a tenant before saving a tenant-scoped override.', 'warn');
      return;
    }
    if (scope === 'AGENT' && !agentId) {
      addToast?.('Pick an agent before saving an agent-scoped override.', 'warn');
      return;
    }
    if (hasErrors) {
      addToast?.('Fix the highlighted fields before saving.', 'warn');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        configScope: scope,
        /* G324 — Always include orgId / tenantId so the backend
         * persists to the right row instead of falling back to the
         * session's default tenant. */
        ...(selectedOrgId ? { orgId: selectedOrgId } : {}),
        ...(scope !== 'ORG' && selectedTenantId ? { tenantId: selectedTenantId } : {}),
        ...(scope === 'AGENT' ? { agentId } : {}),
        evaluationMultiplier:    Number(editor.evaluationMultiplier),
        blockWeight:             Number(editor.blockWeight),
        warnWeight:              Number(editor.warnWeight),
        auditWeight:             Number(editor.auditWeight),
        selfAutonomousThreshold: Number(editor.selfAutonomousThreshold),
        supervisedThreshold:     Number(editor.supervisedThreshold),
        restrictedThreshold:     Number(editor.restrictedThreshold),
        scoreFloor:              Number(editor.scoreFloor),
        windowDays:              Number(editor.scoringWindowDays),
        probationDays:           Number(editor.probationDurationDays),
        trustHistoryRetentionDays: Number(editor.trustHistoryRetentionDays),
        // Backend DTO field is autoRegressionOnBlock — sending `autoRegress`
        // was silently dropped (Jackson ignored the unknown key), so the
        // toggle never persisted. Use the real field name.
        autoRegressionOnBlock:   !!editor.autoRegress,
        // ── Conformance Scoring (The Crossing, Slice B) — exact backend DTO names.
        // Org Admin+ only: omit these fields entirely for lower roles so their
        // ordinary scoring saves don't trip the backend's Org-Admin conformance gate. ──
        ...(isOrgAdmin ? {
          conformanceComparatorMode: editor.conformanceComparatorMode || 'exact_match',
          requiredConformance:       Number(editor.requiredConformance),
          conformanceSampleSize:     Number(editor.conformanceSampleSize),
          conformanceWeight:         Number(editor.conformanceWeight),
          shadowConformanceEnabled:  !!editor.shadowConformanceEnabled,
          shadowSamplingRate:        Number(editor.shadowSamplingRate),
        } : {}),
        // ── Materiality → autonomy rigor (Lever A, V339) — Org Admin only, like conformance.
        ...(isOrgAdmin ? {
          materialityAutonomyCouplingEnabled: !!editor.materialityAutonomyCouplingEnabled,
          materialityThresholdStep:           Number(editor.materialityThresholdStep),
          materialityMinEvalsStep:            Number(editor.materialityMinEvalsStep),
        } : {}),
        // Org Trust Goals require Org Admin (RBAC_RULE_MAP §4.4); omit for
        // lower roles so a Tenant Admin's tenant-scope save isn't 403'd.
        ...(isOrgAdmin && (scope === 'ORG' || scope === 'TENANT') ? {
          targetTrustScore:    Number(editor.targetTrustScore) || null,
          targetAutonomyLevel: editor.targetAutonomyLevel || null,
          orgRiskTolerance:    editor.orgRiskTolerance || null,
          complianceDeadline:  editor.complianceDeadline ? new Date(editor.complianceDeadline).toISOString() : null,
          boardReportNote:     editor.boardReportNote || null,
        } : {}),
      };
      const r = await updateTrustScoringConfig(payload);
      if (r?.ok === false) {
        addToast?.(`Save failed — ${fieldMsg(r) || r.error || 'unknown error'}`, 'error');
      } else {
        addToast?.(`${scope} scoring config saved.`, 'success');
        load();
      }
    } catch (e) {
      addToast?.(`Save failed — ${fieldMsg(e) || e?.message || e}`, 'error');
    } finally { setSaving(false); }
  };

  /* Discard unsaved edits and show the values this scope resolves to again.
   *
   * This used to load a table of platform defaults kept in this file and tell the operator
   * "reset to platform defaults — click Save to persist", while loading violation weights of
   * 5 / 2 / 0.5 against the platform's 3.00 / 0.00 / 0.00. Saving it would have written
   * weights nobody had chosen, under a message saying defaults were being restored.
   *
   * Under org -> tenant -> agent inheritance the useful operation is this one: go back to what
   * this scope actually resolves to. Removing an override altogether is the Delete action,
   * which is a different thing and already exists. */
  const handleReset = () => {
    if (!effective) return;
    seedEditorFrom(effective);
    addToast?.(currentScopeIsCustom
      ? 'Edits discarded — showing the saved values for this scope.'
      : 'Edits discarded — showing the values inherited from the parent scope.', 'info');
  };

  const handleDelete = async () => {
    /* G324 — pass selectedTenantId so the backend deletes the right
     * row instead of relying on session default. */
    const r = await deleteTrustScoringConfig(
      scope,
      scope === 'ORG' ? undefined : selectedTenantId,
      scope === 'AGENT' ? agentId : null,
    );
    if (r?.ok === false) throw new Error(r.error || 'Failed');
    addToast?.(`${scope} override removed — inheriting from parent scope.`, 'success');
    load();
  };

  /* Fallback config for the delete-preview modal — the parent scope's
   * current config (i.e. what the system will inherit FROM after the
   * override is gone). */
  const fallbackConfig = useMemo(() => {
    if (scope === 'AGENT') return allConfigs.find((c) => (c.configScope || '').toUpperCase() === 'TENANT')
                              || allConfigs.find((c) => (c.configScope || '').toUpperCase() === 'ORG')
                              || null;
    if (scope === 'TENANT') return allConfigs.find((c) => (c.configScope || '').toUpperCase() === 'ORG') || null;
    return null;  /* ORG falls back to platform default — no row */
  }, [allConfigs, scope]);

  const currentConfig = allConfigs.find((c) => {
    const cs = (c.configScope || '').toUpperCase();
    if (cs !== scope) return false;
    if (scope === 'AGENT') return c.agentId === agentId;
    return true;
  });

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">
      {/* Inheritance chain */}
      <InheritanceChain
        activeScope={scope}
        customAt={customAt}
        onScopeChange={setScope}
      />

      {/* G324 — Scope-aware selector triple (Org / Tenant / Agent).
          Visibility and read-only state derive from the operator's
          RBAC role. Mirrors V1 lines 1754-1788 but rebuilt in the V2
          design system. */}
      <Card padding="md">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-3">
          Scope target
        </div>
        <div className="grid gap-3" style={{
          gridTemplateColumns: scope === 'AGENT' ? 'repeat(3, 1fr)'
                              : scope === 'TENANT' ? 'repeat(2, 1fr)'
                              : '1fr',
        }}>
          {/* Org picker — always visible (read-only for non-Super-Admin) */}
          <label className="block">
            <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
              <Building2 size={11} /> Organization
              {!isSuperAdmin && <Pill tone="muted" size="sm">Read-only</Pill>}
            </span>
            <select
              value={selectedOrgId || ''}
              onChange={(e) => setSelectedOrgId(e.target.value || null)}
              disabled={!isSuperAdmin}
              className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none disabled:bg-v2-muted disabled:text-v2-ink-muted"
            >
              {orgs.length === 0 && <option value="">— No orgs available —</option>}
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>

          {/* Tenant picker — visible for TENANT / AGENT scopes */}
          {scope !== 'ORG' && (
            <label className="block">
              <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                <Briefcase size={11} /> Tenant
                {!isOrgAdmin && <Pill tone="muted" size="sm">Read-only</Pill>}
              </span>
              <select
                value={selectedTenantId || ''}
                onChange={(e) => setSelectedTenantId(e.target.value || null)}
                disabled={!isOrgAdmin || !selectedOrgId}
                className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none disabled:bg-v2-muted disabled:text-v2-ink-muted"
              >
                {tenants.length === 0 && <option value="">— No tenants —</option>}
                {!selectedTenantId && <option value="">— Select a tenant —</option>}
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}

          {/* Agent picker — visible only for AGENT scope */}
          {scope === 'AGENT' && (
            <label className="block">
              <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                <User size={11} /> Agent
              </span>
              <select
                value={agentId}
                onChange={(e) => { setAgentId(e.target.value); if (e.target.value) onFocusAgent?.(e.target.value); }}
                disabled={!isTenantAdmin || !selectedTenantId}
                className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none disabled:bg-v2-muted disabled:text-v2-ink-muted"
              >
                <option value="">— Select an agent —</option>
                {enriched.map((a) => (
                  <option key={a.id} value={a.id}>{a.name} — score {a.trust ?? '—'}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {/* RBAC hint when scope isn't allowed */}
        {scope === 'ORG' && !isSuperAdmin && (
          <p className="text-[11px] text-v2-warning mt-2 m-0">
            <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />
            Only Super Admin can save Organization-scoped overrides.
          </p>
        )}
        {scope === 'TENANT' && !isOrgAdmin && (
          <p className="text-[11px] text-v2-warning mt-2 m-0">
            <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />
            Only Super Admin / Org Admin can save Tenant-scoped overrides.
          </p>
        )}
      </Card>

      {/* Status banner */}
      {/* G324 — Render the editor / banner only when the operator has
          selected a valid target for the current scope. Otherwise show
          a guidance prompt instead of an empty editor that would save
          nothing. */}
      {!loading && (
        scope === 'ORG' ? !!selectedOrgId
        : scope === 'TENANT' ? !!selectedTenantId
        : !!agentId
      ) && (
        <div className={`rounded-v2-md p-3 border flex items-start gap-2.5 ${
          currentScopeIsCustom
            ? 'bg-v2-success-soft border-v2-success/30 text-v2-success-strong'
            : 'bg-v2-warning-soft border-v2-warning/30 text-v2-warning-strong'
        }`}>
          {currentScopeIsCustom ? <ShieldCheck size={16} className="shrink-0 mt-0.5" /> : <Info size={16} className="shrink-0 mt-0.5" />}
          <div className="flex-1 text-[12.5px]">
            <strong>{currentScopeIsCustom ? 'CUSTOM override' : 'Inheriting from parent scope'}.</strong>{' '}
            {currentScopeIsCustom
              ? `This ${scope.toLowerCase()} has its own scoring config — changes save back to this scope.`
              : `No ${scope.toLowerCase()} override yet. The editor shows the inherited values; save to create a new override.`}
          </div>
          {currentScopeIsCustom && scope !== 'ORG' && (
            <Button variant="secondary" size="sm" iconLeft={<Trash2 size={13} />}
                    onClick={() => setDeleteModal(true)} disabled={saving}>
              Remove override
            </Button>
          )}
        </div>
      )}

      {/* Main editor */}
      {/* G324 — Render the editor / banner only when the operator has
          selected a valid target for the current scope. Otherwise show
          a guidance prompt instead of an empty editor that would save
          nothing. */}
      {!loading && (
        scope === 'ORG' ? !!selectedOrgId
        : scope === 'TENANT' ? !!selectedTenantId
        : !!agentId
      ) && (
        <>
          {/* Evaluation multiplier + preview */}
          <Card padding="md">
            <Card.Header
              title="Evaluation multiplier"
              subtitle="Effective evaluation count = actualEvals × multiplier. Affects how quickly an agent's trust score converges."
            />
            <Card.Body>
              <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <NumericInput
                  label="Multiplier"
                  value={editor.evaluationMultiplier}
                  onChange={(v) => setEditor((e) => ({ ...e, evaluationMultiplier: v }))}
                  step="1" min="1" max="100"
                  error={fieldErrors.evaluationMultiplier}
                />
                <div className="bg-v2-action-soft border border-v2-action/30 rounded-v2-md p-3">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-action-strong mb-1 flex items-center gap-1.5">
                    <Calculator size={11} /> Preview
                  </div>
                  <div className="text-[12px] text-v2-ink mb-1">
                    For 50 evals, 2 blocks, 3 warns:
                  </div>
                  <div className="text-[28px] font-bold text-v2-action-strong">{previewScore}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">/ 100 score</div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Violation weights */}
          <Card padding="md">
            <Card.Header
              title="Violation weights"
              subtitle="How heavily each verdict type counts against the trust score"
            />
            <Card.Body>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <NumericInput
                  label="BLOCK weight"
                  value={editor.blockWeight}
                  onChange={(v) => setEditor((e) => ({ ...e, blockWeight: v }))}
                  borderTone="danger"
                  step="0.5" min="0" max="50"
                  error={fieldErrors.blockWeight}
                />
                <NumericInput
                  label="WARN weight"
                  value={editor.warnWeight}
                  onChange={(v) => setEditor((e) => ({ ...e, warnWeight: v }))}
                  borderTone="warning"
                  step="0.5" min="0" max="50"
                  error={fieldErrors.warnWeight}
                />
                <NumericInput
                  label="AUDIT weight"
                  value={editor.auditWeight}
                  onChange={(v) => setEditor((e) => ({ ...e, auditWeight: v }))}
                  borderTone="cyan"
                  step="0.5" min="0" max="50"
                  error={fieldErrors.auditWeight}
                />
              </div>
            </Card.Body>
          </Card>

          {/* Conformance scoring — The Crossing, Slice B. Governs the autonomy
              ladder → Org Admin+ only (ratified RBAC decision; backend-enforced). */}
          {isOrgAdmin && (
          <Card padding="md">
            <Card.Header
              title="Conformance scoring"
              subtitle="Agreement with the registered deterministic baseline — the third input to the unified Trust Score (eval + violation + conformance)."
            />
            <Card.Body>
              <div className="grid grid-cols-2 gap-3">
                <TextSelect
                  label="Comparator mode"
                  value={editor.conformanceComparatorMode}
                  onChange={(v) => setEditor((e) => ({ ...e, conformanceComparatorMode: v }))}
                  options={[
                    { value: 'exact_match',      label: 'Exact match' },
                    { value: 'equivalence',      label: 'Equivalence' },
                    { value: 'tolerance_banded', label: 'Tolerance-banded' },
                    { value: 'custom',           label: 'Custom' },
                  ]}
                />
                <NumericInput
                  label="Required conformance (%)"
                  value={editor.requiredConformance}
                  onChange={(v) => setEditor((e) => ({ ...e, requiredConformance: v }))}
                  min="0" max="100" step="1"
                  error={fieldErrors.requiredConformance}
                />
                <NumericInput
                  label="Sample size (N runs)"
                  value={editor.conformanceSampleSize}
                  onChange={(v) => setEditor((e) => ({ ...e, conformanceSampleSize: v }))}
                  min="1" max="100000" step="1"
                  error={fieldErrors.conformanceSampleSize}
                />
                <NumericInput
                  label="Weight in Trust Score (0–1)"
                  value={editor.conformanceWeight}
                  onChange={(v) => setEditor((e) => ({ ...e, conformanceWeight: v }))}
                  min="0" max="1" step="0.05"
                  error={fieldErrors.conformanceWeight}
                />
                <TextSelect
                  label="Live shadow scoring"
                  value={editor.shadowConformanceEnabled ? 'on' : 'off'}
                  onChange={(v) => setEditor((e) => ({ ...e, shadowConformanceEnabled: v === 'on' }))}
                  options={[
                    { value: 'off', label: 'Off' },
                    { value: 'on',  label: 'On — score every eligible AutoLet run' },
                  ]}
                />
                <NumericInput
                  label="Shadow sampling rate (0–1)"
                  value={editor.shadowSamplingRate}
                  onChange={(v) => setEditor((e) => ({ ...e, shadowSamplingRate: v }))}
                  min="0" max="1" step="0.05"
                  error={fieldErrors.shadowSamplingRate}
                />
              </div>
              <p className="mt-2 text-[11px] text-v2-ink-muted">
                Weight 0 = off (unified score = eval + violation, today's behaviour). N is how
                many sealed agreements a step needs before it may promote — set it by the
                criticality of the process. <strong>Live shadow scoring</strong> runs a
                suppressed cognitive shadow on each AutoLet production run and scores it
                against the baseline; sampling rate caps the extra cost.
              </p>
            </Card.Body>
          </Card>
          )}

          {/* Materiality → autonomy rigor (Lever A, V339). Governs the autonomy ladder
              → Org Admin+ only (backend-enforced), like conformance. Inert at off / step 0. */}
          {isOrgAdmin && (
          <Card padding="md">
            <Card.Header
              title="Materiality → autonomy rigor (Lever A)"
              subtitle="A more material agent must clear a higher, better-evidenced bar for the same autonomy. Raises ONLY the autonomy thresholds — never the trust score."
            />
            <Card.Body>
              <div className="grid grid-cols-2 gap-3">
                <TextSelect
                  label="Coupling"
                  value={editor.materialityAutonomyCouplingEnabled ? 'on' : 'off'}
                  onChange={(v) => setEditor((e) => ({ ...e, materialityAutonomyCouplingEnabled: v === 'on' }))}
                  options={[
                    { value: 'off', label: 'Off — inert (thresholds unchanged)' },
                    { value: 'on',  label: 'On — materiality steps up the bar' },
                  ]}
                />
                <NumericInput
                  label="Threshold step (score-pts / tier)"
                  value={editor.materialityThresholdStep}
                  onChange={(v) => setEditor((e) => ({ ...e, materialityThresholdStep: v }))}
                  min="0" max="50" step="0.5"
                  error={fieldErrors.materialityThresholdStep}
                />
                <NumericInput
                  label="Min-evals step (evals / tier)"
                  value={editor.materialityMinEvalsStep}
                  onChange={(v) => setEditor((e) => ({ ...e, materialityMinEvalsStep: v }))}
                  min="0" max="1000" step="1"
                  error={fieldErrors.materialityMinEvalsStep}
                />
              </div>
              <p className="mt-2 text-[11px] text-v2-ink-muted">
                Steps apply per tier above immaterial: <strong>Tier 1 ×3, Tier 2 ×2, Tier 3 ×1, Tier 4 ×0</strong>.{' '}
                {editor.materialityAutonomyCouplingEnabled ? (
                  <>For a <strong>Tier 1</strong> agent, self-autonomous now needs <strong>≥ {Math.min(100, Number(editor.selfAutonomousThreshold || 0) + Number(editor.materialityThresholdStep || 0) * 3)}</strong> score
                  {Number(editor.materialityMinEvalsStep || 0) > 0 ? <> and <strong>+{Number(editor.materialityMinEvalsStep) * 3}</strong> more evaluations</> : null}.</>
                ) : (<>Currently <strong>off</strong> — thresholds are unchanged.</>)}{' '}
                The trust score itself is never modified.
              </p>
            </Card.Body>
          </Card>
          )}

          {/* Autonomy thresholds */}
          <Card padding="md">
            <Card.Header
              title="Autonomy thresholds"
              subtitle="Trust-score boundaries the runtime uses to set each agent's autonomy level"
            />
            <Card.Body>
              {/* Stacked visualisation */}
              <div className="mb-4">
                <div className="flex h-3 rounded-v2-pill overflow-hidden bg-v2-surface-muted">
                  {thresholdBars.map((b) => {
                    const w = Math.max(0, b.end - b.start);
                    if (w <= 0) return null;
                    return (
                      <div key={b.key} className={
                        b.tone === 'success' ? 'bg-v2-success'
                        : b.tone === 'warning' ? 'bg-v2-warning'
                        : 'bg-v2-danger'
                      } style={{ width: `${w}%` }} title={`${b.label}: ${b.start}-${b.end}`} />
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[10.5px] mt-1.5">
                  {thresholdBars.slice().reverse().map((b) => (
                    <div key={b.key} className="text-center">
                      <div className="font-semibold text-v2-ink">{b.label}</div>
                      <div className="text-v2-ink-muted">{b.start}-{b.end}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <NumericInput
                  label="Self-Auto (≥)"
                  value={editor.selfAutonomousThreshold}
                  onChange={(v) => setEditor((e) => ({ ...e, selfAutonomousThreshold: v }))}
                  borderTone="success"
                  min="50" max="100"
                  error={fieldErrors.selfAutonomousThreshold}
                />
                <NumericInput
                  label="Supervised (≥)"
                  value={editor.supervisedThreshold}
                  onChange={(v) => setEditor((e) => ({ ...e, supervisedThreshold: v }))}
                  borderTone="warning"
                  min="50" max="100"
                  error={fieldErrors.supervisedThreshold}
                />
                <NumericInput
                  label="Restricted (≥)"
                  value={editor.restrictedThreshold}
                  onChange={(v) => setEditor((e) => ({ ...e, restrictedThreshold: v }))}
                  borderTone="warning"
                  min="0" max="100"
                  error={fieldErrors.restrictedThreshold}
                />
                <NumericInput
                  label="Score floor"
                  value={editor.scoreFloor}
                  onChange={(v) => setEditor((e) => ({ ...e, scoreFloor: v }))}
                  borderTone="danger"
                  min={SCORING_BOUNDS.scoreFloor.min} max={SCORING_BOUNDS.scoreFloor.max}
                  error={fieldErrors.scoreFloor}
                />
              </div>
              <p className="text-[11px] text-v2-ink-muted mt-2">
                The lowest score an agent can be given, between {SCORING_BOUNDS.scoreFloor.min} and{' '}
                {SCORING_BOUNDS.scoreFloor.max}. An agent driven to the very bottom can struggle to
                recover, because the trust gate blocks the runs that would raise it again — a floor
                leaves it somewhere to climb from. The ceiling is deliberate: a floor set high
                enough stops reporting the agent rather than protecting it. Set 0 to see the true
                posture of an agent that is blocking on every call.
              </p>
            </Card.Body>
          </Card>

          {/* Scoring window + probation */}
          <Card padding="md">
            <Card.Header
              title="Scoring window &amp; probation"
              subtitle="How long the rolling evaluation window is, and how probation behaves"
            />
            <Card.Body>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <NumericInput
                  label="Rolling window (days)"
                  value={editor.scoringWindowDays}
                  onChange={(v) => setEditor((e) => ({ ...e, scoringWindowDays: v }))}
                  min="1" max="365"
                  error={fieldErrors.scoringWindowDays}
                />
                <NumericInput
                  label="Probation duration (days)"
                  value={editor.probationDurationDays}
                  onChange={(v) => setEditor((e) => ({ ...e, probationDurationDays: v }))}
                  min="1" max="90"
                  error={fieldErrors.probationDurationDays}
                />
                <NumericInput
                  label="Trust history retention (days)"
                  value={editor.trustHistoryRetentionDays}
                  onChange={(v) => setEditor((e) => ({ ...e, trustHistoryRetentionDays: v }))}
                  min="30" max="7305"
                  error={fieldErrors.trustHistoryRetentionDays}
                />
                <label className="flex items-center gap-2 mt-5 text-[12.5px] text-v2-ink">
                  <input
                    type="checkbox"
                    checked={!!editor.autoRegress}
                    onChange={(e) => setEditor((s) => ({ ...s, autoRegress: e.target.checked }))}
                  />
                  Auto-regress on threshold breach
                </label>
              </div>
            </Card.Body>
          </Card>

          {/* Organisational goals — Org Admin+ only (RBAC_RULE_MAP §4.4) */}
          {isOrgAdmin && (scope === 'ORG' || scope === 'TENANT') && (
            <Card padding="md">
              <Card.Header
                title="Organisational trust goals"
                subtitle="Shown on the board report; used to compute the 'Meets target' indicator"
              />
              <Card.Body>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <NumericInput
                    label="Target score"
                    value={editor.targetTrustScore}
                    onChange={(v) => setEditor((e) => ({ ...e, targetTrustScore: v }))}
                    min="50" max="100"
                    icon={Target}
                    error={fieldErrors.targetTrustScore}
                  />
                  <TextSelect
                    label="Target autonomy"
                    value={editor.targetAutonomyLevel}
                    onChange={(v) => setEditor((e) => ({ ...e, targetAutonomyLevel: v }))}
                    options={[
                      { value: '', label: '— None —' },
                      { value: 'SELF_AUTONOMOUS', label: 'Self-Autonomous' },
                      { value: 'SUPERVISED',      label: 'Supervised' },
                      { value: 'RESTRICTED',      label: 'Restricted' },
                    ]}
                  />
                  <TextSelect
                    label="Risk tolerance"
                    value={editor.orgRiskTolerance}
                    onChange={(v) => setEditor((e) => ({ ...e, orgRiskTolerance: v }))}
                    options={[
                      { value: '', label: '— None —' },
                      { value: 'CONSERVATIVE', label: 'Conservative' },
                      { value: 'MODERATE',     label: 'Moderate' },
                      { value: 'AGGRESSIVE',   label: 'Aggressive' },
                    ]}
                  />
                  <DateInput
                    label="Compliance deadline"
                    value={editor.complianceDeadline}
                    onChange={(v) => setEditor((e) => ({ ...e, complianceDeadline: v }))}
                  />
                </div>
                <label className="block">
                  <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
                    Board report note
                  </span>
                  <textarea
                    value={editor.boardReportNote || ''}
                    onChange={(e) => setEditor((s) => ({ ...s, boardReportNote: e.target.value }))}
                    rows={2}
                    placeholder='e.g. "Q3 priority: drive fleet trust score above 90 ahead of SOC 2 renewal."'
                    className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[12.5px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none resize-y"
                  />
                </label>
              </Card.Body>
            </Card>
          )}

          {/* Save / reset bar */}
          {hasErrors && (
            <p className="text-[11px] text-v2-danger m-0 flex items-center gap-1.5">
              <AlertTriangle size={12} className="shrink-0" />
              Some values are out of range — they're highlighted above. Fix them to save.
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button variant="primary" iconLeft={<Save size={13} />}
                    onClick={handleSave} disabled={saving || hasErrors}>
              {saving ? 'Saving…' : currentScopeIsCustom ? 'Update override' : 'Create override'}
            </Button>
            {/* Says what it does. It read "Reset to defaults" while loading a table of numbers
                kept in this file, three of which were not the platform's. It now re-reads the
                values this scope resolves to, which is a discard, not a reset. */}
            <Button variant="secondary" iconLeft={<RefreshCw size={13} />}
                    onClick={handleReset} disabled={saving}
                    title={currentScopeIsCustom
                      ? 'Discard unsaved edits and show the saved values for this scope again'
                      : 'Discard unsaved edits and show the values inherited from the parent scope again'}>
              Discard edits
            </Button>
          </div>
        </>
      )}

      {/* Formula reference */}
      <Card padding="md">
        <Card.Header
          title="Formula reference"
          subtitle="The exact computation the scoring engine applies to each agent every evaluation"
        />
        <Card.Body>
          <ol className="space-y-3 text-[12.5px] m-0 pl-4 list-decimal">
            <li>
              <code className="bg-v2-surface-muted rounded px-1.5 py-0.5 font-mono-v2 text-[11.5px]">
                effectiveEvals = actualEvals × evaluationMultiplier
              </code>
            </li>
            <li>
              <code className="bg-v2-surface-muted rounded px-1.5 py-0.5 font-mono-v2 text-[11.5px]">
                weightedViolations = blocks × blockWeight + warns × warnWeight + audits × auditWeight
              </code>
            </li>
            <li>
              <code className="bg-v2-surface-muted rounded px-1.5 py-0.5 font-mono-v2 text-[11.5px]">
                violationScore = max(scoreFloor, 100 − (weightedViolations / effectiveEvals) × 100)
              </code>
            </li>
            <li>
              <code className="bg-v2-surface-muted rounded px-1.5 py-0.5 font-mono-v2 text-[11.5px]">
                trustScore = (1 − conformanceWeight) × violationScore + conformanceWeight × conformanceScore × 100
              </code>
              <div className="text-v2-ink-secondary mt-1 text-[11.5px]">
                <code className="bg-v2-surface-muted rounded px-1 py-0.5 font-mono-v2 text-[11px]">conformanceScore</code>
                {' '}is the agent's measured agreement with its registered deterministic baseline (the Crossing).
                At <code className="bg-v2-surface-muted rounded px-1 py-0.5 font-mono-v2 text-[11px]">conformanceWeight = 0</code>
                {' '}(the default) this term is inert and the trust score equals the violation score above. The shadow
                harness that produces the conformance evidence lands in a later Crossing slice.
              </div>
            </li>
            <li className="text-v2-ink-secondary">
              Autonomy level is then assigned by comparing the (unified) trust score to the thresholds above.
              Falling below the restricted threshold for {editor.probationDurationDays} consecutive days triggers
              probation {editor.autoRegress ? 'automatically' : 'only on operator action'}.
            </li>
          </ol>
        </Card.Body>
      </Card>

      {/* Active overrides summary */}
      <Card padding="md">
        <Card.Header
          title="Active overrides"
          subtitle={`${allConfigs.length} persisted scoring config${allConfigs.length === 1 ? '' : 's'} for this tenant`}
        />
        <Card.Body>
          {allConfigs.length === 0 ? (
            <EmptyState
              icon={<Sliders size={20} />}
              title="No custom overrides"
              description="The tenant is using the platform default trust scoring. Create a tenant-scoped override above to begin tuning."
            />
          ) : (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-v2-border-subtle">
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Scope</th>
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Agent</th>
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Thresholds</th>
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Weights</th>
                  <th className="text-right text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {allConfigs.map((c) => (
                  <tr key={`${c.configScope}-${c.agentId || 'tenant'}`} className="border-b border-v2-border-subtle/50">
                    <td className="py-2">
                      <Pill tone={c.configScope === 'ORG' ? 'cyan' : c.configScope === 'TENANT' ? 'success' : 'trust'} size="sm">
                        {c.configScope}
                      </Pill>
                    </td>
                    <td className="py-2 font-mono-v2 text-[11px] text-v2-ink-muted">
                      {c.agentId ? String(c.agentId).slice(0, 8) + '…' : '—'}
                    </td>
                    <td className="py-2 text-[11.5px]">
                      AUTO ≥{c.selfAutonomousThreshold ?? '—'} · SUP ≥{c.supervisedThreshold ?? '—'} · RES ≥{c.restrictedThreshold ?? '—'}
                    </td>
                    <td className="py-2 text-[11.5px]">
                      B{c.blockWeight ?? '—'} · W{c.warnWeight ?? '—'} · A{c.auditWeight ?? '—'}
                    </td>
                    <td className="py-2 text-right font-mono-v2 text-[11px] text-v2-ink-muted">
                      {c.updatedAt ? new Date(c.updatedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {deleteModal && (
        <DeleteScoringOverrideModal
          isOpen
          scope={scope}
          current={currentConfig}
          fallback={fallbackConfig}
          onClose={() => setDeleteModal(false)}
          onSubmit={handleDelete}
        />
      )}
    </div>
  );
}

/* ───── Form primitives ───── */
function NumericInput({ label, value, onChange, step = '1', min, max, borderTone, icon: Icon, error }) {
  const borderClass = error ? 'border-l-2 border-l-v2-danger'
                    : borderTone === 'danger'  ? 'border-l-2 border-l-v2-danger'
                    : borderTone === 'warning' ? 'border-l-2 border-l-v2-warning'
                    : borderTone === 'success' ? 'border-l-2 border-l-v2-success'
                    : borderTone === 'cyan'    ? 'border-l-2 border-l-v2-accent-cyan'
                    : '';
  return (
    <label className="block">
      <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={11} />} {label}
      </span>
      <input
        type="number" step={step} min={min} max={max}
        value={value == null ? '' : value}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        aria-invalid={!!error}
        className={`w-full border ${error ? 'border-v2-danger' : 'border-v2-border-subtle'} ${borderClass} rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none`}
      />
      {error && <span className="block mt-1 text-[10.5px] text-v2-danger">{error}</span>}
    </label>
  );
}

function TextSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">{label}</span>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}
              className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface focus:border-v2-action focus:outline-none">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1 flex items-center gap-1.5">
        <Calendar size={11} /> {label}
      </span>
      <input
        type="date"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface focus:border-v2-action focus:outline-none"
      />
    </label>
  );
}

/* ───── Helpers ───── */

/* Range bounds keyed by EDITOR field name, mirroring TrustScoringConfig
 * entity constraints exactly. Keep in lockstep with the entity + the
 * TrustScoringConfigRequest DTO. */
const SCORING_BOUNDS = {
  evaluationMultiplier:      { min: 1,  max: 100 },
  blockWeight:               { min: 0,  max: 50 },
  warnWeight:                { min: 0,  max: 50 },
  auditWeight:               { min: 0,  max: 50 },
  selfAutonomousThreshold:   { min: 50, max: 100 },
  supervisedThreshold:       { min: 50, max: 100 },
  restrictedThreshold:       { min: 0,  max: 100 },
  // 30, not 100: the top of the score scale and the highest CONFIGURABLE floor are
  // different limits. TrustScoringConfig.SCORE_FLOOR_MAX owns this number; a backend
  // test (TrustScoringConfigBoundsTest) fails if this table drifts from it.
  scoreFloor:                { min: 0,  max: 30 },
  scoringWindowDays:         { min: 1,  max: 365 },
  probationDurationDays:     { min: 1,  max: 90 },
  trustHistoryRetentionDays: { min: 30, max: 7305 },
  requiredConformance:       { min: 0,  max: 100 },
  conformanceSampleSize:     { min: 1,  max: 100000 },
  conformanceWeight:         { min: 0,  max: 1 },
  shadowSamplingRate:        { min: 0,  max: 1 },
  materialityThresholdStep:  { min: 0,  max: 50 },
  materialityMinEvalsStep:   { min: 0,  max: 1000 },
  targetTrustScore:          { min: 50, max: 100 },
};

/* Pull the GlobalExceptionHandler 400 body's per-field messages out of
 * whatever shape the API wrapper surfaces (thrown error or {ok:false}).
 * Safety net behind the client-side validation. */
function fieldMsg(src) {
  const fields = src?.fields || src?.data?.fields || src?.response?.data?.fields || src?.body?.fields;
  if (fields && typeof fields === 'object') {
    const parts = Object.entries(fields).map(([k, v]) => `${k}: ${v}`);
    if (parts.length) return parts.join('; ');
  }
  return null;
}

function clampN(n, lo, hi) {
  const x = Number(n);
  if (!Number.isFinite(x)) return lo;
  return Math.max(lo, Math.min(hi, x));
}

export default ScoringConfigTab;
